import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { useBootstrap } from "../api/queries/useBootstrap";
import { Button } from "../components/ui/button";
import { AccountMenu } from "./AccountMenu";
import { BrandMark } from "./BrandMark";
import { CurrentPlanLabel } from "./CurrentPlanLabel";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { ScopeName, ScopeSwitcher, isScopeKeyedPath } from "./ScopeSwitcher";
import { NavSidebar } from "./NavSidebar";
import { ThemeToggle } from "./ThemeToggle";
import { useCurrentPath, useRouteTitle } from "./useRouteTitle";

export function AppShellDesktop({ children }: { children: ReactNode }) {
  const { data } = useBootstrap();
  const title = useRouteTitle();
  const pathname = useCurrentPath();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={`sticky top-0 flex h-screen shrink-0 flex-col border-r border-border bg-surface-sunken transition-[width] duration-150 ${
          collapsed ? "w-12" : "w-56"
        }`}
      >
        <div className="flex h-11 items-center gap-sm border-b border-border px-sm">
          <Link to="/" className="flex items-center gap-xs text-primary">
            <BrandMark className="h-4 w-6" />
            {collapsed ? null : <span className="type-subhead text-foreground">LevelLoad</span>}
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavSidebar mode={collapsed ? "rail" : "full"} />
        </div>
        <div className="border-t border-border p-xs">
          <Button
            variant="ghost"
            size="icon"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-11 items-center gap-sm border-b border-border bg-background px-md">
          <span className="type-subhead text-foreground">{data?.tenant.name}</span>
          <EnvironmentBadge />
          <div className="flex-1" />
          <ScopeSwitcher />
          <CurrentPlanLabel />
          <ThemeToggle />
          <AccountMenu />
        </header>
        <div className="border-b border-border px-md py-sm">
          <h1 className="type-section text-foreground">
            {title}
            {isScopeKeyedPath(pathname) ? <ScopeName className="ml-xs" /> : null}
          </h1>
        </div>
        {/* A flex column so a full-height page (a register grid) can fill the region. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</main>
      </div>
    </div>
  );
}
